import React, { useMemo } from "react";
import styled, { css } from "styled-components";
import { useAnimatedTab } from "./AnimatedTab.hooks";

interface Tab {
  title: string;
  id: string;
}

interface Props {
  tabs: Tab[];
  activeTab: string;
  gap?: number;
  minWidth?: number;
  className?: string;
  onTabChange: (tabId: string) => void;
}

const TabRoot = styled.div`
  position: relative;
`;

const TabList = styled.div<{ $gap: number }>`
  display: flex;
  align-items: flex-start;
  gap: ${({ $gap }) => $gap}px;
  width: 100%;
`;

const TabButton = styled.button<{ $active: boolean; $minWidth?: number }>`
  display: flex;
  align-items: flex-start;
  position: relative;
  height: 32.97px;
  font-size: 14.59px;
  line-height: 19.45px;
  letter-spacing: 0px;
  cursor: pointer;
  background: none;
  border: none;
  outline: none;
  justify-content: ${({ $minWidth }) =>
    $minWidth && $minWidth > 0 ? "center" : "flex-start"};
  min-width: ${({ $minWidth }) => ($minWidth ? `${$minWidth}px` : "unset")};
  flex: 1;

  ${({ $active }) =>
    $active
      ? css`
          font-weight: bold;
          color: #1f6aff;
        `
      : css`
          font-weight: 600;
          color: #6c727f;
        `}
`;

const TabUnderline = styled.div<{ $width: number; $left: number }>`
  position: absolute;
  bottom: -1px;
  height: 2.43px;
  border-radius: 1px;
  background: #1f6aff;
  transition:
    transform 0.3s cubic-bezier(0.4, 0, 0.2, 1),
    width 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  width: ${({ $width }) => $width}px;
  transform: translateX(${({ $left }) => $left}px);
  pointer-events: none;
`;

const TabButtonUnderline = styled.div`
  width: 100%;
  position: absolute;
  bottom: -1px;
  height: 2px;
  border-radius: 1px;
  background: #1f6aff;
  pointer-events: none;
`;

export function AnimatedTab({
  tabs,
  activeTab,
  gap = 32,
  minWidth = 0,
  className,
  onTabChange,
}: Props) {
  const { current, addRefs } = useAnimatedTab(activeTab);

  const { width, left } = useMemo(() => {
    if (!current) return { width: 0, left: 0 };
    return { width: current.offsetWidth, left: current.offsetLeft };
  }, [current]);

  const handleClick = (tabId: string) => {
    onTabChange(tabId);
  };

  return (
    <TabRoot>
      <TabList $gap={gap} className={className}>
        {tabs.map((tab, i) => (
          <TabButton
            key={i}
            $active={tab.id === activeTab}
            $minWidth={minWidth}
            onClick={() => handleClick(tab.id)}
            ref={(instance) => {
              addRefs(instance, tab.id);
            }}
          >
            {tab.title}
            {tab.id === activeTab && current && current.offsetWidth === 0 && (
              <TabButtonUnderline />
            )}
          </TabButton>
        ))}
      </TabList>

      {activeTab && <TabUnderline $width={width} $left={left} />}
    </TabRoot>
  );
}
